import { signal, computed } from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';

import { plainToClass } from 'class-transformer';
import { debounceTime, Subscription } from 'rxjs';
import { QuestionType, SystemSourceId, LanguageVariableToken, QuestionPrefix, Source } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { QuestionEditor } from '../question-editor.model';
import { ISourceForm, SourceForm } from '../sources/source-form.model';
import { IEditorForm } from '../survey-editor/editor-form.interface';
import { ILocalizationForm } from '../survey-editor/localization-form.model';
import { IQuestionForm } from '../type-forms/question-form-group.interface';
import { QuestionFormSourceVariable } from '../type-forms/question-form-source-variable.model';
import { QuestionOptionForm } from '../type-forms/question-option-form.model';

export interface EditingSourceVariable {
  sourceId: string;
  sourceTitle: string;
  variable: FormGroup<IQuestionForm>;
}

export class SurveySources {
  activeIndex$ = signal<number>(-1);
  active$ = computed(() => {
    this.changeListener?.unsubscribe();
    if (this.activeIndex$() > -1) {
      this.listenToTitleChange();
      return this.editable.controls[this.activeIndex$()];
    }
  });
  activeVariable$ = signal<EditingSourceVariable>(null);
  editable$ = signal<Source[]>([]);
  count$ = computed(() => this.editable$().length);
  questions$ = signal<QuestionEditor[]>([]);
  systemSourceControl: FormGroup<ISourceForm> | null = null;

  private changeListener: Subscription;

  constructor(private surveyForm: FormGroup<IEditorForm>) {
    this.systemSourceControl = this.createSystemSource().form;
  }

  get all(): FormGroup<ISourceForm>[] {
    if (this.systemSourceControl) {
      return [this.systemSourceControl, ...this.editable.controls];
    }

    return this.editable.controls;
  }

  get editable(): FormArray<FormGroup<ISourceForm>> {
    return this.surveyForm.get('sources') as FormArray<FormGroup<ISourceForm>>;
  }

  get nextId(): string {
    const sourceIds: number[] = this.editable$().map((source) => +source.id.replace(/^\D+/g, ''));
    const lastId = sourceIds.length === 0 ? 0 : Math.max.apply(null, sourceIds);

    return `${QuestionPrefix.Source}_${lastId + 1}`;
  }

  get lastSourceVariableId(): number {
    const svQuestionIds: number[] = this.questions$().map((sv) => +sv.id.replace(/^\D+/g, ''));
    return svQuestionIds.length === 0 ? 0 : Math.max.apply(null, svQuestionIds);
  }

  editVariable(id: string): void {
    const parentSourceCtrl = this.editable.controls.find((sourceCtrl) => sourceCtrl.value.variables.some((variable) => variable.id === id));
    const variableCtrl = parentSourceCtrl.controls.variables.controls.find((variable) => variable.value.id === id);

    this.activeVariable$.set({
      sourceId: parentSourceCtrl.value.id,
      sourceTitle: parentSourceCtrl.value.title,
      variable: variableCtrl
    });
  }

  push(title: string): string {
    const newSourceId = this.nextId;
    const newSourceForm = new SourceForm(newSourceId, title);
    this.pushToList(newSourceForm);
    return newSourceId;
  }

  pushModel(source: Source): string {
    const newSourceId = this.nextId;
    const newSourceForm = new SourceForm(newSourceId);
    newSourceForm.patch(source);
    this.pushToList(newSourceForm);
    return newSourceId;
  }

  pushVariable(variable: Question): void {
    const newVariable = new QuestionFormSourceVariable();
    const nextId = this.lastSourceVariableId + 1;
    newVariable.init(variable);
    newVariable.initEmpty(nextId);

    this.listenToTokenChange(newVariable.form);

    this.active$().controls.variables.push(newVariable.form);
    this.questions$.update((questions) => [...questions, newVariable.form.value as QuestionEditor]);
  }

  updateVariable(index: number, variable: Question): void {
    const updatedVariable = new QuestionFormSourceVariable();
    updatedVariable.init(variable);

    this.active$().controls.variables.setControl(index, updatedVariable.form);
    this.questions$.set(this.editable.controls.flatMap((sourceCtrl) => sourceCtrl.value.variables as QuestionEditor[]));

    if (this.activeVariable$()?.variable.value.id === variable.id) {
      this.editVariable(variable.id);
    }
  }

  removeActive(): void {
    const indexToRemove = this.activeIndex$();
    this.removeAt(indexToRemove);

    if (indexToRemove === 0) {
      // Required to trigger compute logic
      this.activeIndex$.set(-1);
      if (this.editable.controls.length > 0) {
        this.activeIndex$.set(0);
      }
    } else {
      this.activeIndex$.set(indexToRemove - 1);
    }
  }

  removeVariable(index: number): void {
    const idToRemove = this.active$().controls.variables.at(index).controls.id;
    this.active$().controls.variables.removeAt(index);
    this.questions$.update((questions) => questions.filter((q) => q.id !== idToRemove.value));
  }

  patch(sources: Source[] = []): void {
    const systemSource = sources.find((source) => source.isSystem);

    if (systemSource) {
      const systemSourceForm = new SourceForm(systemSource.id);
      systemSourceForm.patch(systemSource);
      this.systemSourceControl = systemSourceForm.form;
    } else {
      this.systemSourceControl = this.createSystemSource().form;
    }

    const editableSources = sources.filter((source) => !source.isSystem);

    editableSources.forEach((source) => {
      const sourceForm = new SourceForm(source.id);
      sourceForm.patch(source);
      this.editable.push(sourceForm.form);
      sourceForm.form.controls.variables.controls.forEach((variableCtrl) => this.listenToTokenChange(variableCtrl));
    });

    this.editable$.set(editableSources);
    this.questions$.set(editableSources.flatMap((source) => source.variables as QuestionEditor[]));
  }

  map(): Source[] {
    const editableSources = this.editable.value.map((source) => {
      const seenTokens = new Set<string>();
      const filteredVariables = (source.variables ?? []).filter((variable) => {
        const token = variable.variableToken?.trim().toLowerCase();
        if (!token) {
          return true;
        }

        if (seenTokens.has(token)) {
          return false;
        }

        seenTokens.add(token);

        return true;
      });
      return plainToClass(Source, { ...source, variables: filteredVariables });
    });

    if (!this.systemSourceControl) {
      return editableSources;
    }

    return [plainToClass(Source, this.systemSourceControl.value), ...editableSources];
  }

  addLanguageVariableOption(languageCode: string): void {
    const languageVariableCtrl = this.getLanguageVariableControl();

    if (!languageVariableCtrl) {
      const languageVariable = this.createLanguageVariable([languageCode]);
      this.systemSourceControl.controls.variables.push(languageVariable.form);
      return;
    }

    const option = new QuestionOptionForm();

    const id = `opt_${languageVariableCtrl.controls.options.length + 1}`;

    option.form.patchValue({
      id,
      text: languageCode,
      value: languageCode
    });

    languageVariableCtrl.controls.options.push(option.form);
  }

  removeLanguageVariableOption(languageCode: string): void {
    const languageVariableCtrl = this.getLanguageVariableControl();

    if (!languageVariableCtrl) {
      return;
    }

    const optionIndex = languageVariableCtrl.controls.options.controls.findIndex((optionCtrl) => optionCtrl.controls.value.value === languageCode);

    if (optionIndex === -1) {
      return;
    }

    languageVariableCtrl.controls.options.removeAt(optionIndex);

    if (languageVariableCtrl.controls.options.length === 0) {
      const variableIndex = this.systemSourceControl.controls.variables.controls.findIndex(
        (variableCtrl) => variableCtrl.controls.variableToken.value === LanguageVariableToken
      );

      if (variableIndex > -1) {
        this.systemSourceControl.controls.variables.removeAt(variableIndex);
      }
    }
  }

  private createSystemSource(): SourceForm {
    const systemSourceForm = new SourceForm(SystemSourceId, SystemSourceId);

    systemSourceForm.form.patchValue({
      isSystem: true
    });

    const loc = this.surveyForm.get('loc') as FormArray<FormGroup<ILocalizationForm>>;
    const codes = loc.controls.map((c) => c.controls.lc.value);

    if (codes.length) {
      const langVariable = this.createLanguageVariable(codes);
      systemSourceForm.form.controls.variables.push(langVariable.form);
    }

    return systemSourceForm;
  }

  private createLanguageVariable(languageCodes: string[]): QuestionFormSourceVariable {
    const variable = new QuestionFormSourceVariable();

    variable.form.patchValue({
      id: `${QuestionPrefix.SystemSourceVariable}--LANG`,
      code: SystemSourceId,
      variableToken: LanguageVariableToken,
      variableRequiredType: 'REQUIRED',
      type: QuestionType.SingleChoice
    });

    for (let i = 0; i < languageCodes.length; i++) {
      const code = languageCodes[i];
      const option = new QuestionOptionForm();
      const optionId = `opt_${i + 1}`;

      option.form.patchValue({
        id: optionId,
        text: code,
        value: code
      });

      variable.options.push(option.form);
    }

    return variable;
  }

  private getLanguageVariableControl(): FormGroup<IQuestionForm> | null {
    return (
      this.systemSourceControl.controls.variables.controls.find((variableCtrl) => variableCtrl.controls.variableToken.value === LanguageVariableToken) ?? null
    );
  }

  private removeAt(blockIndex: number): void {
    this.editable.removeAt(blockIndex);
    this.editable$.update((sources) => sources.filter((_, index) => index !== blockIndex));
    this.questions$.set(this.editable$().flatMap((source) => source.variables as QuestionEditor[]));
  }

  private pushToList(newSourceForm: SourceForm): void {
    this.editable.push(newSourceForm.form);
    this.editable$.update((sources) => [...sources, plainToClass(Source, newSourceForm.form.value)]);

    this.activeIndex$.set(this.count$() - 1);
  }

  private listenToTokenChange(variableToCheck: FormGroup<IQuestionForm>): void {
    variableToCheck.controls.variableToken.valueChanges.pipe(debounceTime(1000)).subscribe(() => {
      const qIndex = this.questions$().findIndex((q) => q.id === variableToCheck.controls.id.value);
      this.questions$.update((questions) => {
        questions.splice(qIndex, 1, variableToCheck.value as QuestionEditor);
        return [...questions];
      });
    });
  }

  private listenToTitleChange(): void {
    this.editable.controls[this.activeIndex$()]
      .get('title')
      .valueChanges.pipe(debounceTime(1000))
      .subscribe((blockTitle) => {
        this.editable$.update((blocks) => {
          blocks[this.activeIndex$()].title = blockTitle;
          return [...blocks];
        });
      });
  }
}
